/**
 * Order Flow plugin: LightweightCharts renderer for the order-flow layers.
 *
 * Renders:
 * - Absorption: 1-point absorption zones (side blue/orange, outline + fill, entry-read recolor)
 * - Aggressive Orders: whole-point aggressive-order levels (buy/sell dashed lines)
 * - Context: prior-session profile fills + POC / value-area / core-area lines
 * - Market Structure: swing marks and break lines (PPAS color scheme)
 * - Rotation Reach: renko rotation p50/p70/p90 levels
 *
 * The order-flow model is DESCRIPTIVE: a filled band marks where passive orders were observed,
 * a dashed line where aggressive orders were observed. Color says WHO (buyers blue, sellers
 * orange); the shape says WHAT. Nothing here is a prediction.
 */

import CONFIG from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

// Order-flow presentation RGB (reference section 8): color is WHO.
const RGB_BUYERS = '0, 128, 255';     // buyers (defended or aggressive): blue
const RGB_SELLERS = '255, 96, 0';     // sellers (defended or aggressive): orange
const RGB_POC = '255, 200, 50';       // point of control: gold
const RGB_CORE = '230, 140, 40';      // core area: orange
const RGB_VALUE = '0, 180, 170';      // value area: teal
const RGB_RANGE = '160, 160, 160';    // profile range: gray

// Memo for the context profile's per-session Globex-open epochs (pane views are
// rebuilt every frame; the set of dates changes at most once per session). Keyed by
// session_date ISO string; bounded by the served history window (~30 sessions).
const _sessionOpenMemo = new Map();

// Base absorption zone fill opacity: C# ZoneFillAlpha = 60/255 (confirmed A.10,
// 2026-10-02). The entry-read recolor spans derive 2x/1x from this base.
const ABS_FILL_ALPHA = 60 / 255;

// Round half to even (banker's rounding): JS Math.round is round-half-up, which skews the
// aggressive-order width quantization at the 0.25/0.75 t boundaries (ref 8.2).
function roundHalfEven(x) {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (Math.abs(diff - 0.5) < 1e-9) {
    // Exactly halfway: snap to the even neighbor.
    return (floor % 2 === 0) ? floor : floor + 1;
  }
  return Math.round(x);
}

class OrderFlowPlugin {
  constructor(chart, series, options = {}) {
    this._chart = chart;
    this._series = series;
    this._options = {
      apiUrl: CONFIG.apiUrl,
      // Absorption / aggressive colors are side-driven (buyers blue, sellers orange): see the
      // RGB_BUYERS / RGB_SELLERS module constants. No significance gradient (reference 8).
      // Market-structure colors (PPAS scheme: marks colored by SIDE, lines by break dir)
      structBullColor: 'rgba(8, 153, 129, 0.95)',    // swing highs / upward breaks / bull state
      structBearColor: 'rgba(242, 54, 69, 0.95)',    // swing lows / downward breaks / bear state
      structShowDetail: false,  // child toggle: tier-1 swing dots AND tier-1 (dotted) break
                                // lines; tier-2/3 marks and lines always draw
      // Rotation reach (renko rotation p50/p70/p90) colors
      reach50Color: 'rgba(45, 212, 191, 0.9)',          // teal - median (typical reach)
      reach70Color: 'rgba(148, 163, 184, 0.9)',         // slate - 70th percentile
      reach90Color: 'rgba(234, 179, 8, 0.9)',           // amber - 90th percentile
      // Rotation leg (zig-zag) colors
      legUpColor: '#22c55e',                            // green - up leg
      legDownColor: '#ef4444',                          // red - down leg
      fontSize: 10,
      ...options
    };

    this._enabled = {
      absorption: false,
      focusLevels: false,      // Aggressive-order levels (internal name kept; root = aggressive_orders)
      contextProfile: false,   // Prior-session profile fills + POC/value/core lines
      marketStructure: false,  // Tier-1/2 swing pivots + BOS/CHoCH breaks + bias (descriptive)
      rotationLegs: false,     // Renko rotation legs (zig-zag connecting pivots)
      rotationReach: false,    // Renko rotation p50/p70/p90 reach levels (per pivot + forward)
    };
    this._data = {
      absorption: null,        // Absorption zones (root absorption) from /snapshot + SSE
      focusLevels: null,       // Aggressive-order levels (root aggressive_orders) from /snapshot + SSE
      context: null,           // Context block (scalar levels) from /snapshot + SSE
      contextHistory: null,    // Historic per-session context blocks from /context-history
                               // (REST only; refreshed on day rollover, never by SSE)
      marketStructure: null,   // Tier pivot + break + bias events from /market-structure + SSE
      rotations: null,         // {thresholds, legs} from /api/es/renko-rotations + es:renko:{size} SSE
    };
    // Snapshot seq watermark for the SSE resync discipline (orderflow_events gap -> refetch).
    this._lastSeq = null;
    this._renkoBrickSize = 8.0;  // served renko size the rotations overlay reads (8.0 | 4.0)
    // Rotations SSE buffer-replay (mirrors RenkoRotationsPage loadingRef/pendingRotationsRef):
    // a renko_rotations push arriving while the REST fetch is in flight is stashed and replayed
    // once the fetch settles, so a pivot confirmed mid-load isn't dropped or clobbered by REST.
    // (_renkoSSEUnsub and _rotationsDateRange are intentionally lazily-undefined.)
    this._rotationsLoading = false;      // a rotations REST fetch is in flight
    this._pendingRotationsPush = null;   // a push that arrived mid-load, replayed on settle
    this._rotationsLoadPromise = null;   // coalesce concurrent _loadRotations for the same key
    this._primitiveAttached = false;
    this._requestUpdate = null;
    this._candles = null;  // Store candles for snapping zones to candle boundaries
  }

  /**
   * Inject static data for a feature without any API/SSE wiring.
   * Used by the docs MiniChart to render fixture snapshots; live charts
   * should keep using the enable*() paths.
   * @param {string} feature - 'absorption' | 'focusLevels' | 'context'
   *                           | 'marketStructure'
   * @param {Array|Object} data - payload in the same shape the API serves
   */
  setStaticData(feature, data) {
    if (!(feature in this._data)) {
      console.warn(`[OrderFlow] setStaticData: unknown feature "${feature}"`);
      return;
    }
    this._data[feature] = data;
    this._enabled[feature] = data != null;
    this._triggerUpdate();
  }

  /**
   * Update the candles data for snapping zones to candle boundaries
   * Call this whenever candles change (timeframe switch, new data, etc.)
   * @param {Array} candles - Array of candles with time property
   */
  updateCandles(candles) {
    if (candles && candles.length > 0) {
      // Sort candles by time and store
      this._candles = [...candles].sort((a, b) => a.time - b.time);
    } else {
      this._candles = null;
    }
    this._triggerUpdate();
  }

  // ISeriesPrimitive interface methods
  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
    this._primitiveAttached = true;
  }

  detached() {
    this._primitiveAttached = false;
    this._requestUpdate = null;
    this._disconnectSSE();
    this._disconnectRenkoSSE();
  }

  paneViews() {
    const anyEnabled = Object.values(this._enabled).some(v => v);
    if (!anyEnabled) {
      return [];
    }
    return [new OrderFlowPaneView(this._options, this._chart, this._series, this._data, this._enabled, this._candles)];
  }

  // Market structure: tier-1/2 swing pivots + BOS/CHoCH break lines + per-tier bias. Served
  // from the resolved key via /events + SSE snapshot (mirrors focus, no client
  // polling). Display/context layer only; labels describe realized breaks, never predictions.
  async enableMarketStructure() {
    if (this._enabled.marketStructure) return;
    try {
      await this.fetchMarketStructure();
      this._enabled.marketStructure = true;
      this._triggerUpdate();
      console.log('[OrderFlow] Market structure enabled:', this._data.marketStructure?.length || 0, 'events');
      this._connectSSE();
    } catch (error) {
      console.error('[OrderFlow] Failed to enable market structure:', error);
    }
  }

  disableMarketStructure() {
    this._enabled.marketStructure = false;
    this._data.marketStructure = null;
    if (!this._anySSEFeatureEnabled()) {
      this._disconnectSSE();
    }
    this._triggerUpdate();
    console.log('[OrderFlow] Market structure disabled');
  }

  isMarketStructureEnabled() {
    return this._enabled.marketStructure === true;
  }

  setStructShowDetail(show) {
    const next = !!show;
    if (this._options.structShowDetail === next) return;
    this._options.structShowDetail = next;
    this._triggerUpdate();
  }

  _anySSEFeatureEnabled() {
    return this._enabled.absorption ||
           this._enabled.focusLevels ||
           this._enabled.contextProfile ||
           this._enabled.marketStructure;
  }

  // SSE connection for real-time order-flow updates via unified client.
  // TWO event types on the 'orderflow' channel (each needs its OWN subscribe call so
  // _registerEvents runs on the already-connected channel, else EventSource silently drops it):
  //   orderflow_snapshot - the full snapshot body (wholesale-replace all three roots)
  //   orderflow_events    - granular lifecycle batch; used here only to track the seq and refetch
  //                         the snapshot on a seq gap (wholesale-replace discipline is kept)
  _connectSSE() {
    // Already connected - do nothing (idempotent)
    if (this._sseUnsubs && this._sseUnsubs.length > 0) {
      return;
    }

    console.log('[OrderFlow] Connecting SSE (unified)');

    this._sseUnsubs = [];

    // orderflow_snapshot: the full snapshot body. Wholesale-replace all three roots so a
    // cap-evicted or broken level can never linger client-side; the incoming snapshot is
    // authoritative. Context rides the same snapshot (ingested outside any gate).
    this._sseUnsubs.push(
      unifiedSSE.subscribe('orderflow', 'orderflow_snapshot', (data) => {
        try {
          this._applySnapshot(data);
          console.log('[OrderFlow] SSE snapshot:', this._data.focusLevels?.length || 0, 'aggressive levels');
        } catch (err) {
          console.error('[OrderFlow] Error handling SSE snapshot:', err);
        }
      })
    );

    // orderflow_events: the granular lifecycle batch. For now we do not fold individual events
    // into the roots (wholesale-replace stays the single discipline); we only watch the seq to
    // detect a gap and refetch the authoritative snapshot when one is seen.
    this._sseUnsubs.push(
      unifiedSSE.subscribe('orderflow', 'orderflow_events', (data) => {
        try {
          const latest = (data && (data.latest_seq ?? data.seq));
          if (latest == null) return;
          if (this._lastSeq != null && latest > this._lastSeq + 1) {
            // Gap in the event stream: the snapshot may be stale. Refetch wholesale.
            this._refetchSnapshot();
          }
          this._lastSeq = latest;
        } catch (err) {
          console.error('[OrderFlow] Error handling SSE events:', err);
        }
      })
    );

    console.log('[OrderFlow] SSE connected (unified)');
  }

  // Apply a full COMBINED snapshot body (the SSE orderflow_snapshot payload: {meta, absorption,
  // aggressive_orders, context}): wholesale-replace all three roots and track the seq. REST is
  // per-family since the endpoint split; this combined shape arrives over SSE only. The empty
  // state {meta, source} (no roots) clears to empty arrays.
  _applySnapshot(body) {
    if (!body) return;
    this._data.absorption = Array.isArray(body.absorption) ? body.absorption : [];
    this._data.focusLevels = Array.isArray(body.aggressive_orders) ? body.aggressive_orders : [];
    const prevCtxDate = this._data.context && this._data.context.session_date;
    this._data.context = body.context || null;
    this._maybeRollContextHistory(prevCtxDate);
    const seq = body.meta && body.meta.seq;
    if (seq != null) this._lastSeq = seq;
    this._triggerUpdate();
  }

  // Day rollover: the history root is REST-only (SSE snapshots never carry it), so a
  // live-context session_date change is the one signal to refetch it (the just-ended
  // session's blob lands in the per-day cache at settlement).
  _maybeRollContextHistory(prevCtxDate) {
    const newDate = this._data.context && this._data.context.session_date;
    if (this._enabled.contextProfile && prevCtxDate && newDate && newDate !== prevCtxDate) {
      this.fetchContextHistory().catch((err) =>
        console.error('[OrderFlow] context history rollover refetch error:', err));
    }
  }

  // Refetch every currently enabled family from its per-family endpoint (on a seq gap).
  // Failure is logged and left for the next event/refresh.
  async _refetchSnapshot() {
    try {
      await this._loadEnabledFamilies();
    } catch (err) {
      console.error('[OrderFlow] family refetch error:', err);
    }
  }

  _disconnectSSE() {
    if (this._sseUnsubs && this._sseUnsubs.length > 0) {
      this._sseUnsubs.forEach(unsub => unsub());
      this._sseUnsubs = [];
      console.log('[OrderFlow] SSE disconnected');
    }
  }

  // Public API - Enable/disable individual features

  // Aggressive-order levels (whole-point big-print folds on same-side imbalance stacks).
  // Served from the single live snapshot (/snapshot), kept fresh by the orderflow SSE.
  async enableFocusLevels() {
    if (this._enabled.focusLevels) return;

    try {
      const ok = await this.fetchAggressive();
      if (!ok) return; // Fetch failed - allow retry on next effect run

      this._enabled.focusLevels = true;
      this._connectSSE();
      this._triggerUpdate();
      console.log('[OrderFlow] Aggressive levels enabled:', this._data.focusLevels?.length || 0, 'levels');
    } catch (error) {
      console.error('[OrderFlow] Failed to enable aggressive levels:', error);
    }
  }

  disableFocusLevels() {
    this._enabled.focusLevels = false;
    this._data.focusLevels = null;
    if (!this._anySSEFeatureEnabled()) {
      this._disconnectSSE();
    }
    this._triggerUpdate();
    console.log('[OrderFlow] Aggressive levels disabled');
  }

  // Context Profile overlay: prior-session profile fills + POC / value-area / core-area lines.
  // Rides the same /snapshot body (context root) and the orderflow SSE.
  async enableContextProfile() {
    if (this._enabled.contextProfile) return;
    try {
      // History fetch runs alongside; it is tolerant of 404/empty (pre-deploy or
      // mid-warm backend) and never blocks the live layer.
      const [ok] = await Promise.all([this.fetchContext(), this.fetchContextHistory()]);
      if (!ok) return;
      this._enabled.contextProfile = true;
      this._connectSSE();
      this._triggerUpdate();
    } catch (error) {
      console.error('[OrderFlow] Failed to enable context profile:', error);
    }
  }

  disableContextProfile() {
    this._enabled.contextProfile = false;
    this._data.context = null;
    this._data.contextHistory = null;
    if (!this._anySSEFeatureEnabled()) {
      this._disconnectSSE();
    }
    this._triggerUpdate();
  }

  // ── Rotation overlay (renko legs zig-zag + p50/p70/p90 reach levels) ──────────────────────
  // The two layers share ONE rotations payload + ONE renko SSE subscription. The data loads on
  // first enable and is released only when BOTH layers are off, so toggling one never tears down
  // the other.
  // Shared rotations loader: re-fetches only when the chart's date range changes (keyed on the
  // date range + brick size), so the overlay extends when the user loads an earlier date and both
  // layers reuse one fetch + one renko SSE.
  async _loadRotations(startDate, endDate) {
    // Key includes the brick size so a size switch (same date range) invalidates the cache and
    // re-fetches, mirroring the date-range-key behaviour for loading an earlier date.
    const key = `${startDate}-${endDate}-${this._renkoBrickSize}`;
    if (this._rotationsDateRange === key && this._data.rotations) return true; // already loaded
    // Coalesce concurrent loads: enableRotationReach and enableRotationLegs both call this in the
    // same effect pass, so without this they'd double-fetch and race the loading flag. Share the
    // in-flight promise for the same key; a size/date change makes a new key -> new fetch.
    if (this._rotationsLoadPromise && this._rotationsLoadPromise.key === key) {
      return this._rotationsLoadPromise.promise;
    }
    const promise = (async () => {
      const ok = await this.fetchRotations(startDate, endDate);
      if (!ok) return false; // fetch failed - allow retry on next effect run
      this._rotationsDateRange = key;
      return true;
    })().finally(() => {
      if (this._rotationsLoadPromise && this._rotationsLoadPromise.key === key) {
        this._rotationsLoadPromise = null;
      }
    });
    this._rotationsLoadPromise = { key, promise };
    return promise;
  }

  // Switch the renko size the overlay reads (called from the two enable methods). On a real
  // change, tear down the old SSE so _connectRenkoSSE rebinds the new channel; the _loadRotations
  // key (which embeds the size) then forces a re-fetch. No-op when the size is unchanged.
  _setRenkoBrickSize(brickSize) {
    if (brickSize == null || brickSize === this._renkoBrickSize) return;
    this._renkoBrickSize = brickSize;
    // Drop any in-flight load / buffered push for the OLD size so a stale size-8 push can never
    // replay onto freshly-fetched size-4 data.
    this._pendingRotationsPush = null;
    this._rotationsLoading = false;
    this._rotationsLoadPromise = null;
    this._disconnectRenkoSSE();
  }

  _maybeReleaseRotations() {
    if (!this._enabled.rotationReach && !this._enabled.rotationLegs) {
      this._disconnectRenkoSSE();
      this._data.rotations = null;
      this._rotationsDateRange = null;
    }
  }

  // NOTE: NO early-return-if-enabled - the GEXChart effect calls this on every candle-range
  // change, and _loadRotations re-fetches when the range key moves (so loading an earlier date
  // extends the overlay across the new range).
  async enableRotationReach(startDate, endDate, candles = null, brickSize = null) {
    try {
      this._setRenkoBrickSize(brickSize);   // size switch (if any) before connect/load
      // Subscribe to the renko SSE EAGERLY, before the REST await: this puts es:renko:{size}
      // into the unified connection's channel set now (decoupled from REST latency), so the
      // server starts forwarding renko_rotations promptly and no live push is missed while the
      // fetch is in flight. Idempotent (guarded by _renkoSSEUnsub). If the load then fails the
      // draw gate stays off (rotationReach false) and the effect retries next candle.
      this._connectRenkoSSE();
      if (!(await this._loadRotations(startDate, endDate))) return;
      this._enabled.rotationReach = true;
      this._triggerUpdate();
    } catch (error) {
      console.error('[OrderFlow] Failed to enable rotation reach:', error);
    }
  }

  disableRotationReach() {
    this._enabled.rotationReach = false;
    this._maybeReleaseRotations();
    this._triggerUpdate();
  }

  async enableRotationLegs(startDate, endDate, candles = null, brickSize = null) {
    try {
      this._setRenkoBrickSize(brickSize);   // size switch (if any) before connect/load
      this._connectRenkoSSE();              // eager subscribe (see enableRotationReach)
      if (!(await this._loadRotations(startDate, endDate))) return;
      this._enabled.rotationLegs = true;
      this._triggerUpdate();
    } catch (error) {
      console.error('[OrderFlow] Failed to enable rotation legs:', error);
    }
  }

  disableRotationLegs() {
    this._enabled.rotationLegs = false;
    this._maybeReleaseRotations();
    this._triggerUpdate();
  }

  async fetchRotations(startDate, endDate) {
    // start_date+end_date => the custom multi-day resolve (legs across the loaded candle range);
    // the renko SSE then merges the live current session on top. No start_date = current session.
    const url = new URL(`${this._options.apiUrl}/api/es/renko-rotations`);
    if (startDate) url.searchParams.set('start_date', startDate);
    if (endDate && endDate !== startDate) url.searchParams.set('end_date', endDate);
    const reqSize = this._renkoBrickSize;                       // size this request is built for
    url.searchParams.set('brick_size', reqSize);                // selected served size (8 | 4)
    this._rotationsLoading = true;                              // gate live pushes to the buffer
    try {
      const response = await authFetch(url);
      if (!response.ok) {
        console.warn('[OrderFlow] fetchRotations failed:', response.status);
        return false;
      }
      const data = await response.json();
      // Drop a stale result if a size switch superseded this fetch while it was in flight: else a
      // late size-4 response could clobber size-8 data the SSE has already reconnected to (and vice
      // versa). The caller treats false as "not loaded", so the current size re-fetches/serves.
      if (this._renkoBrickSize !== reqSize) return false;
      const body = data.rotations || {};
      this._data.rotations = { thresholds: body.thresholds, legs: body.legs || [] };
      return true;
    } catch (error) {
      console.error('[OrderFlow] fetchRotations error:', error);
      return false;
    } finally {
      this._rotationsLoading = false;
      // Replay a push that arrived while the REST round-trip was in flight (default current-session
      // view only, and only if still size-consistent), so a pivot confirmed mid-load isn't dropped
      // or clobbered by the REST body just applied above. The buffered push is >= as fresh.
      if (this._pendingRotationsPush && this._renkoBrickSize === reqSize) {
        const push = this._pendingRotationsPush;
        this._pendingRotationsPush = null;
        this._applyRotationsPush(push);
      } else {
        this._pendingRotationsPush = null;
      }
    }
  }

  // Merge a live renko_rotations push into the overlay. The push is CURRENT-SESSION only, so keep
  // the historic legs already loaded over the chart's range and refresh just the current session +
  // thresholds (MERGE, not clobber). Shared by the SSE handler and the mid-load buffer replay.
  // The renko forwarder broadcasts the raw {type, data} publish, so the v2
  // envelope sits at d.data = {meta, rotations}.
  _applyRotationsPush(d) {
    const body = d?.data?.rotations;
    if (!body || !body.thresholds) return;
    const sseLegs = body.legs || [];
    const cut = sseLegs.length ? Math.min(...sseLegs.map((l) => l.start_ts)) : Infinity;
    const prior = (this._data.rotations?.legs || []).filter((l) => l.start_ts < cut);
    this._data.rotations = { thresholds: body.thresholds, legs: [...prior, ...sseLegs] };
    this._triggerUpdate();
  }

  // The reach overlay rides the renko SSE channel (es:renko:8.0, event 'renko_rotations'),
  // which is SEPARATE from the 'orderflow' channel _connectSSE manages. Its unsub is tracked on its
  // own so disabling reach never disturbs the other order flow layers' subscription.
  _connectRenkoSSE() {
    if (this._renkoSSEUnsub) return; // idempotent
    // Match the Python publisher's channel: f"es:renko:{8.0}" == 'es:renko:8.0' (JS `${8.0}` -> '8',
    // so format with one decimal, as useLiveRenkoBricks.js does). Re-bound on size change via
    // _setRenkoBrickSize -> _disconnectRenkoSSE.
    const channel = `es:renko:${Number(this._renkoBrickSize).toFixed(1)}`;
    this._renkoSSEUnsub = unifiedSSE.subscribe(channel, 'renko_rotations', (d) => {
      try {
        if (!d?.data?.rotations?.thresholds) return;
        // Buffer pushes that land while a REST fetch is in flight; fetchRotations replays the last
        // one on settle (else REST would clobber it). Otherwise apply immediately.
        if (this._rotationsLoading) {
          this._pendingRotationsPush = d;
          return;
        }
        this._applyRotationsPush(d);
      } catch (err) {
        console.error('[OrderFlow] Error handling renko_rotations SSE:', err);
      }
    });
  }

  _disconnectRenkoSSE() {
    // Drop any buffered/in-flight push state so it can't replay after teardown or onto a new size.
    this._pendingRotationsPush = null;
    this._rotationsLoading = false;
    this._rotationsLoadPromise = null;
    if (this._renkoSSEUnsub) {
      try { this._renkoSSEUnsub(); } catch (e) { /* ignore */ }
      this._renkoSSEUnsub = null;
    }
  }

  // Absorption zones (1-point buckets where market orders traded into wicks and were absorbed).
  // Served from the single live snapshot (/snapshot); kept fresh by the orderflow SSE. Levels
  // carry start times, so there is no date-range fetch (the whole live session is one snapshot).
  async enableAbsorption() {
    if (this._enabled.absorption) return;

    try {
      const ok = await this.fetchAbsorption();
      if (!ok) return; // Fetch failed - allow retry on next effect run

      this._enabled.absorption = true;
      this._connectSSE();
      this._triggerUpdate();
      console.log('[OrderFlow] Absorption enabled:', this._data.absorption?.length || 0, 'zones');
    } catch (error) {
      console.error('[OrderFlow] Failed to enable absorption:', error);
    }
  }

  disableAbsorption() {
    this._enabled.absorption = false;
    this._data.absorption = null;
    if (!this._anySSEFeatureEnabled()) {
      this._disconnectSSE();
    }
    this._triggerUpdate();
    console.log('[OrderFlow] Absorption disabled');
  }

  disable() {
    this._enabled = {
      absorption: false, focusLevels: false,
      contextProfile: false,
    };
    this._data = {
      absorption: null, focusLevels: null, context: null, contextHistory: null,
    };
    this._lastSeq = null;
    this._triggerUpdate();
    console.log('[OrderFlow] All disabled');
  }

  isEnabled(feature) {
    if (feature) return this._enabled[feature];
    return Object.values(this._enabled).some(v => v);
  }

  async update() {
    // Refresh whatever order-flow layers are on from their per-family endpoints.
    await this._loadEnabledFamilies();
    if (this._enabled.marketStructure) {
      await this.fetchMarketStructure();
    }
    this._triggerUpdate();
  }

  _triggerUpdate() {
    if (this._requestUpdate) {
      this._requestUpdate();
    }
  }

  /**
   * Check fills against historical candles
   * @param {Array} candles - Array of candles with time, high, low
   */
  checkHistoricalFills(candles) {
    if (!candles || candles.length === 0) return;

    let fillCount = 0;

    // Sort candles by time to process in order
    const sortedCandles = [...candles].sort((a, b) => a.time - b.time);

    // Debug: Log candle data info (only in development)
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      const sampleCandle = sortedCandles[0];
      console.log(`[OrderFlow] checkHistoricalFills called with ${sortedCandles.length} candles, sample:`, {
        time: sampleCandle?.time,
        high: sampleCandle?.high,
        low: sampleCandle?.low,
        volume: sampleCandle?.volume
      });
    }

    // Aggressive-level and absorption lifecycle are computed entirely by the server-side reference
    // model on 1000-contract volume candles; no client-side fill checking is needed. Kept as a
    // no-op so the live-tick entry points stay stable across chart timeframes.

    if (fillCount > 0) {
      this._triggerUpdate();
    }
  }

  /**
   * Check if a new candle fills any AOI levels.
   * Call this with candle OHLC to mark levels as filled.
   * All active AOI layers (absorption, focus) compute fill status
   * on the backend, so there is no client-side fill logic here; kept as the
   * shared live-tick entry point.
   * @param {number} high - Candle high price
   * @param {number} low - Candle low price
   * @param {number} open - Candle open price (optional, for imbalance body checks)
   * @param {number} close - Candle close price (optional, for imbalance body checks)
   * @returns {boolean} - True if any levels were filled
   */
  checkForFills() {
    // Aggressive-level and absorption lifecycle are computed by the server-side reference model.
    // No client-side fill checking (kept as the shared live-tick entry point).
    return false;
  }

  // Data fetching

  // -- Per-family + market-structure fetching -------------------------------

  // Shared per-family GET. Each endpoint returns {meta, <root>} or the empty state
  // {meta, source} (no root). Returns the parsed body on success, null on failure.
  async _fetchFamily(path) {
    const url = new URL(`${this._options.apiUrl}/api/indicators/orderflow/${path}`);
    try {
      const response = await authFetch(url);
      if (!response.ok) {
        console.warn(`[OrderFlow] fetch ${path} failed:`, response.status);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error(`[OrderFlow] fetch ${path} error:`, error);
      return null;
    }
  }

  _trackSeq(body) {
    const seq = body && body.meta && body.meta.seq;
    if (seq != null && seq > (this._lastSeq || 0)) this._lastSeq = seq;
  }

  // GET /absorption -> _data.absorption (empty state clears the root).
  async fetchAbsorption() {
    const body = await this._fetchFamily('absorption');
    if (!body) return false;
    this._data.absorption = Array.isArray(body.absorption) ? body.absorption : [];
    this._trackSeq(body);
    this._triggerUpdate();
    return true;
  }

  // GET /aggressive-orders -> _data.focusLevels (internal name for aggressive_orders).
  async fetchAggressive() {
    const body = await this._fetchFamily('aggressive-orders');
    if (!body) return false;
    this._data.focusLevels = Array.isArray(body.aggressive_orders) ? body.aggressive_orders : [];
    this._trackSeq(body);
    this._triggerUpdate();
    return true;
  }

  // GET /context -> _data.context (the Context Profile's data root).
  async fetchContext() {
    const body = await this._fetchFamily('context');
    if (!body) return false;
    const prevCtxDate = this._data.context && this._data.context.session_date;
    this._data.context = body.context || null;
    this._maybeRollContextHistory(prevCtxDate);
    this._trackSeq(body);
    this._triggerUpdate();
    return true;
  }

  // GET /context-history -> _data.contextHistory: historic per-session context blocks
  // {session_date, prior_session} ascending. A null body (route absent pre-deploy) and
  // an empty sessions list (backend mid-warm) both land as []. Anchors are precomputed
  // HERE, once per fetch, never in the per-frame draw path. endSec chain: entry i ends
  // at entry i+1's open; the last entry's endSec stays null and resolves at draw time
  // to the live block's open (zero-width for the live-coincident entry by design).
  async fetchContextHistory() {
    const body = await this._fetchFamily('context-history?days=30');
    const sessions = (body && Array.isArray(body.sessions)) ? body.sessions : [];
    const entries = [];
    for (const s of sessions) {
      if (!s || !s.session_date || !s.prior_session) continue;
      entries.push({
        session_date: s.session_date,
        prior_session: s.prior_session,
        startSec: this._sessionOpenSecFor(s.session_date),
        endSec: null,
      });
    }
    entries.sort((a, b) => (a.session_date < b.session_date ? -1 : 1));
    for (let i = 0; i + 1 < entries.length; i++) {
      entries[i].endSec = entries[i + 1].startSec;
    }
    this._data.contextHistory = entries;
    this._triggerUpdate();
    return true;
  }

  // Fetch every currently enabled family (enable paths, update(), seq-gap refetch).
  async _loadEnabledFamilies() {
    const jobs = [];
    if (this._enabled.absorption) jobs.push(this.fetchAbsorption());
    if (this._enabled.focusLevels) jobs.push(this.fetchAggressive());
    if (this._enabled.contextProfile) jobs.push(this.fetchContext());
    if (jobs.length > 0) await Promise.all(jobs);
    return true;
  }

  // GET /api/indicators/orderflow/market-structure -> {meta, market_structure}. Separate from the
  // snapshot (its own family, unchanged shape). Wholesale-replace the overlay.
  async fetchMarketStructure() {
    const url = new URL(`${this._options.apiUrl}/api/indicators/orderflow/market-structure`);
    try {
      const response = await authFetch(url);
      if (!response.ok) {
        console.warn('[OrderFlow] fetchMarketStructure failed:', response.status);
        return false;
      }
      const data = await response.json();
      this._data.marketStructure = data.market_structure || [];
      return true;
    } catch (error) {
      console.error('[OrderFlow] fetchMarketStructure error:', error);
      return false;
    }
  }
}


class OrderFlowPaneView {
  constructor(options, chart, series, data, enabled, candles) {
    this._options = options;
    this._chart = chart;
    this._series = series;
    this._data = data;
    this._enabled = enabled;
    this._candles = candles;
  }

  zOrder() {
    return 'top'; // Draw above candles
  }

  renderer() {
    return new OrderFlowRenderer(this._options, this._chart, this._series, this._data, this._enabled, this._candles);
  }
}


class OrderFlowRenderer {
  constructor(options, chart, series, data, enabled, candles) {
    this._options = options;
    this._chart = chart;
    this._series = series;
    this._data = data;
    this._enabled = enabled;
    this._candles = candles;
  }

  /**
   * Find the candle that contains the given timestamp.
   * Returns the candle whose time is <= timestamp (the containing candle).
   * @param {number} timestamp - Unix timestamp in seconds
   * @returns {object|null} - The containing candle or null
   */
  _findCandleForTimestamp(timestamp) {
    if (!this._candles || this._candles.length === 0) return null;

    let left = 0;
    let right = this._candles.length - 1;

    // If timestamp is before all candles, return first candle
    if (timestamp <= this._candles[0].time) {
      return this._candles[0];
    }

    // If timestamp is after all candles, return last candle
    if (timestamp >= this._candles[right].time) {
      return this._candles[right];
    }

    // Binary search for the candle whose time is <= timestamp
    while (left < right) {
      const mid = Math.floor((left + right + 1) / 2);
      if (this._candles[mid].time <= timestamp) {
        left = mid;
      } else {
        right = mid - 1;
      }
    }

    // Return the containing candle (time <= timestamp)
    return this._candles[left];
  }

  /**
   * Bitmap-space x of the LEFT and RIGHT edges of the candle that CONTAINS `ts`.
   * timeScale.timeToCoordinate(time) returns a bar's CENTER, not its edge, so drawing a
   * segment start/end at the bare coordinate lands it half a bar off the candle boundary
   * (and inconsistently between the band drawers). Edges = center ± half a bar's width.
   * Bars are spaced uniformly by index (timeScale barSpacing), so this is exact for BOTH
   * time candles and volume candles. Returns null when the containing candle can't be
   * resolved on the current scale (scrolled out of range) so callers can clamp to the canvas.
   * @param {number} ts - Unix timestamp in seconds
   * @param {number} hpr - horizontal pixel ratio
   * @returns {{left:number, right:number}|null}
   */
  _candleEdgesForTime(ts, hpr) {
    const candle = this._findCandleForTimestamp(ts);
    if (!candle) return null;
    const timeScale = this._chart.timeScale();
    const center = timeScale.timeToCoordinate(candle.time);
    if (center === null) return null;
    const half = (timeScale.options().barSpacing || 6) / 2;
    return { left: (center - half) * hpr, right: (center + half) * hpr };
  }

  /**
   * Bitmap-space x of the right edge of the most recent candle = "now". Live (not-broken)
   * references extend to here so they terminate exactly at the latest candle's boundary.
   */
  _rightEdgePx(hpr, scope) {
    if (this._candles && this._candles.length) {
      const e = this._candleEdgesForTime(this._candles[this._candles.length - 1].time, hpr);
      if (e) return e.right;
    }
    const timeScale = this._chart.timeScale();
    const vr = timeScale.getVisibleRange();
    if (vr) {
      const c = timeScale.timeToCoordinate(vr.to);
      if (c !== null) return (c + (timeScale.options().barSpacing || 6) / 2) * hpr;
    }
    return scope.bitmapSize.width;
  }

  // Normalize a timestamp (epoch seconds / ms / ns) to epoch seconds.
  _toSec(v) {
    if (v == null) return null;
    let s = v;
    if (s > 1e15) s = Math.floor(s / 1e9);
    else if (s > 1e12) s = Math.floor(s / 1e3);
    return s;
  }

  // Draw-start anchor for the context profile: the session's Globex open (18:00 ET on the
  // calendar day before session_date, Saturday walked back to Friday), matching the prior-day
  // level anchor. The wire start_time (the IB start, 9:30 ET) stays spec-correct server-side
  // and is only the fallback when session_date is absent. DST-safe: the ET offset is probed
  // at noon UTC (transitions happen at 2 AM ET, never between noon and 18:00).
  _contextStartSec(context) {
    const sd = context.session_date;
    if (!sd) return this._toSec(context.start_time);
    return this._sessionOpenSecFor(sd);
  }

  // The Globex-open epoch (seconds) for a session_date: 18:00 ET on the calendar day
  // before, Saturday walked back to Friday. Map-memoized: the live block AND every
  // historic entry resolve through here (the history set is ~30 dates).
  _sessionOpenSecFor(sd) {
    let sec = _sessionOpenMemo.get(sd);
    if (sec !== undefined) return sec;
    const d = new Date(sd + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() - 1);
    if (d.getUTCDay() === 6) d.setUTCDate(d.getUTCDate() - 1);  // Saturday -> Friday
    const y = d.getUTCFullYear(), m = d.getUTCMonth(), day = d.getUTCDate();
    const etHour = parseInt(new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour: '2-digit', hour12: false,
    }).format(new Date(Date.UTC(y, m, day, 12, 0, 0))), 10);
    const offsetHours = etHour - 12;  // -5 EST, -4 EDT
    sec = Date.UTC(y, m, day, 18 - offsetHours, 0, 0) / 1000;
    _sessionOpenMemo.set(sd, sec);
    return sec;
  }

  // Bitmap-space [xStart, xEnd] for a time span [startTime, endTime|null]. endTime null = live,
  // extended to the present (latest candle's right edge). Returns null when the span is entirely
  // off the right of the view or its start cannot be resolved. Off-left starts clamp to the canvas.
  _xSpan(startTime, endTime, hpr, scope, visibleRange) {
    if (startTime == null) return null;
    const effectiveEnd = (endTime == null) ? (visibleRange ? visibleRange.to + 300 : startTime + 3600) : endTime;
    if (visibleRange && (effectiveEnd < visibleRange.from || startTime > visibleRange.to + 600)) return null;
    const se = this._candleEdgesForTime(startTime, hpr);
    let xStart = se ? Math.round(se.left)
      : ((visibleRange && startTime <= visibleRange.from) ? 0 : null);
    if (xStart === null) return null;
    let xEnd;
    if (endTime == null) {
      xEnd = Math.round(this._rightEdgePx(hpr, scope));
    } else {
      const ee = this._candleEdgesForTime(endTime, hpr);
      xEnd = ee ? Math.round(ee.right)
        : ((visibleRange && endTime >= visibleRange.to) ? scope.bitmapSize.width : 0);
    }
    if (xEnd < xStart) xEnd = xStart;
    return { xStart, xEnd };
  }

  draw(target) {
    if (!this._chart || !this._series) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const timeScale = this._chart.timeScale();
      const visibleRange = timeScale.getVisibleRange();
      if (!visibleRange) return;

      // Each layer draws into the SAME bitmap-coordinate callback. Isolate every phase in its
      // own try/catch so a runtime error in one layer (e.g. an edge-case coordinate in the
      // focus-level band draw) cannot abort the callback and freeze the OTHER layers'
      // rendering - that coupling let one layer's error stop the others from updating.
      const drawPhase = (name, enabled, fn) => {
        if (!enabled) return;
        try {
          fn();
        } catch (err) {
          console.error(`[OrderFlow] Draw error in ${name} (other layers continue):`, err);
        }
      };

      // Context profile fills (drawn FIRST, under the levels: faint prior-session profile bands).
      drawPhase('contextProfile',
        this._enabled.contextProfile && (this._data.context || this._data.contextHistory),
        () => this._drawContextProfile(ctx, scope));

      // Context profile lines (POC/value/core) over the fills.
      drawPhase('contextProfileLines',
        this._enabled.contextProfile && (this._data.context || this._data.contextHistory),
        () => this._drawContextProfileLines(ctx, scope));

      // Absorption zones (side-colored bands)
      drawPhase('absorption', this._enabled.absorption && this._data.absorption,
        () => this._drawAbsorption(ctx, scope));

      // Aggressive-order levels (side-colored dashed lines)
      drawPhase('focusLevels', this._enabled.focusLevels && this._data.focusLevels,
        () => this._drawFocusLevels(ctx, scope));

      // Draw market structure (tier-1/2 pivots + BOS/CHoCH break lines + bias chips)
      drawPhase('marketStructure', this._enabled.marketStructure && this._data.marketStructure,
        () => this._drawMarketStructure(ctx, scope));

      // Draw renko rotation legs (zig-zag connecting pivots) UNDER the reach lines
      drawPhase('rotationLegs', this._enabled.rotationLegs && this._data.rotations,
        () => this._drawRotationLegs(ctx, scope));

      // Draw renko rotation reach levels (p50/p70/p90 per pivot + forward projection)
      drawPhase('rotationReach', this._enabled.rotationReach && this._data.rotations,
        () => this._drawReachLevels(ctx, scope));

    });
  }

  // Draw absorption zones (reference 8.1). A band from the bottom of its lowest row to the top of
  // its highest row, colored by SIDE (buyers blue = buyers defended = support; sellers orange =
  // sellers defended = resistance): color says WHO, not strength. Outline from the zone anchor to
  // the candle it became known on; fill from there to the present. Broken zones render at half the
  // fill alpha and STILL extend to the present (still watched). Entry reads recolor the band over
  // their span. Descriptive only.
  _drawAbsorption(ctx, scope) {
    if (!this._data.absorption || this._data.absorption.length === 0) return;

    const timeScale = this._chart.timeScale();
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const visibleRange = timeScale.getVisibleRange();
    const minHeight = 4;

    for (const abs of this._data.absorption) {
      const side = abs.side;  // 'buy' = buyers defended (support), 'sell' = sellers defended
      const rgb = (side === 'buy') ? RGB_BUYERS : RGB_SELLERS;
      const priceHigh = abs.upper ?? abs.price_high;   // new field `upper`; fixture fallback
      const priceLow = abs.lower ?? abs.price_low;     // new field `lower`; fixture fallback
      if (priceHigh == null || priceLow == null) continue;

      const anchorTime = this._toSec(abs.anchor_time);
      const startTime = this._toSec(abs.start_time);
      const knownTime = (startTime != null) ? startTime : anchorTime;
      if (knownTime == null) continue;

      const isBroken = abs.state === 'broken';
      const isProvisional = abs.state === 'provisional';

      // Fill span: from the candle the zone became known on to the present (end_time is ignored;
      // broken zones still extend to the present, ref 11). Provisional/settled/broken all extend.
      const fillSpan = this._xSpan(knownTime, null, hpr, scope, visibleRange);
      if (!fillSpan) continue;

      // lower/upper are the band's lowest/highest row PRICES; draw half a tick
      // (0.125pt) beyond each so the band spans row bottom to row top (ref 4.3).
      const yHigh = this._series.priceToCoordinate(priceHigh + 0.125);
      const yLow = this._series.priceToCoordinate(priceLow - 0.125);
      if (yHigh === null || yLow === null) continue;

      const yTopRaw = yHigh * vpr;
      const yBottomRaw = yLow * vpr;
      const rawHeight = yBottomRaw - yTopRaw;
      const rectHeight = Math.max(rawHeight, minHeight);
      const yCenter = (yTopRaw + yBottomRaw) / 2;
      const yTop = yCenter - rectHeight / 2;

      const baseAlpha = isBroken ? (ABS_FILL_ALPHA / 2) : ABS_FILL_ALPHA;

      ctx.save();

      // Fill: known candle -> present.
      if (fillSpan.xEnd > fillSpan.xStart) {
        ctx.fillStyle = `rgba(${rgb}, ${baseAlpha})`;
        ctx.fillRect(fillSpan.xStart, yTop, fillSpan.xEnd - fillSpan.xStart, rectHeight);
      }

      // Outline from the anchor to the PRESENT (ref 8.1: the outline starts at the anchor;
      // "between the anchor and that point the zone is outlined only" means the stretch
      // before the known candle has no fill, while the whole band stays bordered, matching
      // the ATAS render). Provisional zones use a dashed outline in the side color.
      const outlineStart = (anchorTime != null) ? Math.min(anchorTime, knownTime) : knownTime;
      const outlineSpan = this._xSpan(outlineStart, null, hpr, scope, visibleRange);
      if (outlineSpan && outlineSpan.xEnd > outlineSpan.xStart) {
        ctx.strokeStyle = `rgba(${rgb}, 0.9)`;
        ctx.lineWidth = Math.max(1, hpr);
        if (isProvisional) ctx.setLineDash([Math.round(6 * hpr), Math.round(4 * hpr)]);
        ctx.strokeRect(outlineSpan.xStart, yTop, outlineSpan.xEnd - outlineSpan.xStart, rectHeight);
        ctx.setLineDash([]);
      }

      // Provisional fill span also carries the dashed outline (forming reference treatment).
      if (isProvisional && fillSpan.xEnd > fillSpan.xStart) {
        ctx.strokeStyle = `rgba(${rgb}, 0.9)`;
        ctx.lineWidth = Math.max(1, hpr);
        ctx.setLineDash([Math.round(6 * hpr), Math.round(4 * hpr)]);
        ctx.strokeRect(fillSpan.xStart, yTop, fillSpan.xEnd - fillSpan.xStart, rectHeight);
        ctx.setLineDash([]);
      }

      // Entry-read recolor spans (ref 8.1). Takeover = the DEFENDER side color (blue when the
      // approach came from above, orange when from below) at 2x base fill alpha; Pressing = the
      // ATTACKER color (orange from above, blue from below) at 1x. Span from the read start to its
      // end, or the present when end is null.
      const reads = Array.isArray(abs.entry_reads) ? abs.entry_reads : [];
      for (const read of reads) {
        const rStart = this._toSec(read.start_time);
        const rEnd = this._toSec(read.end_time);  // null -> present
        if (rStart == null) continue;
        const rSpan = this._xSpan(rStart, rEnd, hpr, scope, visibleRange);
        if (!rSpan || rSpan.xEnd <= rSpan.xStart) continue;
        const fromAbove = read.from === 'above';
        let readRgb;
        let readAlpha;
        if (read.kind === 'takeover') {
          // Defender color: blue when price came down from above, orange when it came up.
          // Ref 8.1: twice the ZONE fill's opacity (halved on a broken zone via baseAlpha).
          readRgb = fromAbove ? RGB_BUYERS : RGB_SELLERS;
          readAlpha = Math.min(1, 2 * baseAlpha);
        } else if (read.kind === 'pressing') {
          // Attacker color: orange when price came down from above, blue when it came up.
          readRgb = fromAbove ? RGB_SELLERS : RGB_BUYERS;
          readAlpha = baseAlpha;
        } else {
          continue;
        }
        ctx.fillStyle = `rgba(${readRgb}, ${readAlpha})`;
        ctx.fillRect(rSpan.xStart, yTop, rSpan.xEnd - rSpan.xStart, rectHeight);
      }

      ctx.restore();

      // No zone label (user preference 2026-10-02): the band itself carries the read;
      // ref 8.1 label text is available in the payload for tooltips if ever wanted.
    }
  }

  // Draw aggressive-order levels (reference 8.2). A dashed line at the whole point from its first
  // fold candle (start_time) to the present. Color by SIDE (buyers blue, sellers orange). Weight
  // and opacity scale with strength relative to the strongest served level of both sides. Broken
  // aggressive levels are never served and not shown (guarded client-side too). Descriptive only.
  _drawFocusLevels(ctx, scope) {
    const levels = this._data.focusLevels;
    if (!levels || levels.length === 0) return;

    const timeScale = this._chart.timeScale();
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return;

    // t = strength / max(1, max strength across ALL served levels, both sides).
    let maxStrength = 1;
    for (const f of levels) {
      if (f.state === 'broken') continue;
      maxStrength = Math.max(maxStrength, f.strength || 0);
    }

    for (const f of levels) {
      // Broken never arrives from the server; skip defensively (do not render broken).
      if (f.state === 'broken') continue;

      const startTime = this._toSec(f.start_time);
      if (startTime == null) continue;

      const span = this._xSpan(startTime, null, hpr, scope, visibleRange);  // live -> present
      if (!span) continue;

      const price = f.price;
      if (price == null) continue;
      const y = this._series.priceToCoordinate(price);
      if (y === null || y === undefined) continue;

      const side = f.side;  // 'buy' | 'sell'
      const rgb = (side === 'buy') ? RGB_BUYERS : RGB_SELLERS;

      const t = Math.min((f.strength || 0) / maxStrength, 1.0);
      // width = 1 + round_half_even(2t) (1 for t<=0.25, 2 below 0.75, 3 from 0.75).
      const widthPts = 1 + roundHalfEven(2 * t);
      // opacity = 0.25 + 0.5t quantized to alpha steps of 16/255, clamped 64..192, then /255.
      const alpha255 = Math.min(192, Math.max(64, Math.round((0.25 + 0.5 * t) * 255 / 16) * 16));
      const opacity = alpha255 / 255;

      const yPx = Math.round(y * vpr);

      ctx.save();
      ctx.strokeStyle = `rgba(${rgb}, ${opacity})`;
      ctx.lineWidth = Math.max(1, Math.round(widthPts * vpr));
      // Aggressive-order lines are ALWAYS dashed, distinct from absorption's filled bands.
      ctx.setLineDash([Math.round(7 * hpr), Math.round(5 * hpr)]);
      ctx.beginPath();
      ctx.moveTo(Math.round(span.xStart), yPx);
      ctx.lineTo(Math.round(span.xEnd), yPx);
      ctx.stroke();
      ctx.setLineDash([]);

      // No line label (user preference 2026-10-02): weight/opacity already carry
      // strength; ref 8.2 label text stays available in the payload for tooltips.
      ctx.restore();
    }
  }

  // Resolve the context blocks to draw, oldest first: the historic per-session entries
  // (endSec chain precomputed at fetch; the last entry's null endSec resolves to the
  // live block's open, so the live-coincident entry collapses to zero width), then the
  // live block (to end_time or present, the only one that draws labels).
  _contextBlocks() {
    const blocks = [];
    const live = this._data.context;
    const liveStart = live ? this._contextStartSec(live) : null;
    for (const h of (this._data.contextHistory || [])) {
      if (!h || !h.prior_session) continue;
      const endSec = h.endSec != null ? h.endSec : liveStart;  // null -> to present
      if (endSec != null && endSec <= h.startSec) continue;   // zero-width: skip
      blocks.push({ prior: h.prior_session, startSec: h.startSec, endSec, withLabels: false });
    }
    if (live && live.prior_session && liveStart != null) {
      blocks.push({
        prior: live.prior_session,
        startSec: liveStart,
        endSec: this._toSec(live.end_time),  // null -> present
        withLabels: true,
      });
    }
    return blocks;
  }

  // Draw the context profile fills (reference 8.3), one block per session: historic
  // entries then the live block. Each block's scalar levels fill its session span: the
  // rest of the range gray, the value area teal, the core area orange, the POC row
  // gold, all faint (POC slightly stronger). Descriptive only. Each block is guarded
  // so one bad entry cannot abort the rest.
  _drawContextProfile(ctx, scope) {
    const blocks = this._contextBlocks();
    if (!blocks.length) return;
    const timeScale = this._chart.timeScale();
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return;
    for (const b of blocks) {
      try {
        this._drawContextFillsBlock(ctx, scope, visibleRange, b);
      } catch (err) {
        console.error('[OrderFlow] context fill draw error (entry skipped):', err);
      }
    }
  }

  _drawContextFillsBlock(ctx, scope, visibleRange, block) {
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const prior = block.prior;
    const span = this._xSpan(block.startSec, block.endSec, hpr, scope, visibleRange);
    if (!span || span.xEnd <= span.xStart) return;
    const width = span.xEnd - span.xStart;

    // Fill a price band [lo, hi] over the span with an rgba.
    const fillBand = (lo, hi, rgb, alpha) => {
      if (lo == null || hi == null) return;
      let yLo = this._series.priceToCoordinate(lo);
      let yHi = this._series.priceToCoordinate(hi);
      if (yLo === null || yHi === null) return;
      const yTop = Math.min(yLo, yHi) * vpr;
      const yBot = Math.max(yLo, yHi) * vpr;
      const h = Math.max(yBot - yTop, 1);
      ctx.fillStyle = `rgba(${rgb}, ${alpha})`;
      ctx.fillRect(span.xStart, yTop, width, h);
    };

    ctx.save();
    // The rest of the range (psl..psh) gray, faint. Value area and core overpaint it.
    fillBand(prior.psl, prior.psh, RGB_RANGE, 0.06);
    // Value area (val..vah) teal.
    fillBand(prior.val, prior.vah, RGB_VALUE, 0.08);
    // Core area (cal..cah) orange.
    fillBand(prior.cal, prior.cah, RGB_CORE, 0.08);
    // POC row: a one-tick-tall band at the POC, gold, slightly stronger.
    if (prior.poc != null) {
      fillBand(prior.poc - 0.125, prior.poc + 0.125, RGB_POC, 0.14);
    }
    ctx.restore();
  }

  // Draw the context profile lines (reference 8.3): POC gold, vah/val teal, cah/cal
  // orange, one block per session over the same spans as the fills. Labels draw for
  // the LIVE block only (historic labels would clutter every session boundary); line
  // + label styling mirrors the prior-day levels (GreeksHeatmapPlugin drawLine):
  // solid 1.5px, boxed label at the line's right end, names joined within half a tick.
  _drawContextProfileLines(ctx, scope) {
    const blocks = this._contextBlocks();
    if (!blocks.length) return;
    const timeScale = this._chart.timeScale();
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return;
    for (const b of blocks) {
      try {
        this._drawContextLinesBlock(ctx, scope, visibleRange, b);
      } catch (err) {
        console.error('[OrderFlow] context line draw error (entry skipped):', err);
      }
    }
  }

  _drawContextLinesBlock(ctx, scope, visibleRange, block) {
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const span = this._xSpan(block.startSec, block.endSec, hpr, scope, visibleRange);
    if (!span) return;

    const prior = block.prior || {};

    // Collect lines to draw as {name, price, rgb}.
    const lines = [];
    if (prior.poc != null) lines.push({ name: 'poc', price: prior.poc, rgb: RGB_POC });
    if (prior.vah != null) lines.push({ name: 'vah', price: prior.vah, rgb: RGB_VALUE });
    if (prior.val != null) lines.push({ name: 'val', price: prior.val, rgb: RGB_VALUE });
    if (prior.cah != null) lines.push({ name: 'cah', price: prior.cah, rgb: RGB_CORE });
    if (prior.cal != null) lines.push({ name: 'cal', price: prior.cal, rgb: RGB_CORE });
    if (!lines.length) return;

    ctx.save();
    for (const line of lines) {
      const y = this._series.priceToCoordinate(line.price);
      if (y === null || y === undefined) continue;
      const yPx = Math.round(y * vpr);
      ctx.strokeStyle = `rgba(${line.rgb}, 0.9)`;
      ctx.lineWidth = 1.5 * hpr;
      ctx.beginPath();
      ctx.moveTo(Math.round(span.xStart), yPx);
      ctx.lineTo(Math.round(span.xEnd), yPx);
      ctx.stroke();
    }

    if (!block.withLabels) {
      ctx.restore();
      return;
    }

    // Labels: group levels within half a tick (0.125) of one price, join their names.
    const groups = [];
    for (const line of lines) {
      let g = groups.find((grp) => Math.abs(grp.price - line.price) <= 0.125);
      if (!g) { g = { price: line.price, names: [], rgb: line.rgb }; groups.push(g); }
      g.names.push(line.name);
    }
    const fontSize = Math.round(12 * hpr);
    const padding = 4 * hpr;
    ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (const g of groups) {
      const y = this._series.priceToCoordinate(g.price);
      if (y === null || y === undefined) continue;
      const yPx = Math.round(y * vpr);
      const label = `${g.names.join(' ')} ${Number(g.price).toFixed(2)}`;
      const textWidth = ctx.measureText(label).width;
      const labelX = span.xEnd - padding * 2;
      if (labelX > span.xStart + textWidth + padding * 4) {
        ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
        ctx.fillRect(
          labelX - textWidth - padding,
          yPx - fontSize / 2 - padding,
          textWidth + padding * 2,
          fontSize + padding * 2
        );
        ctx.fillStyle = `rgba(${g.rgb}, 1)`;
        ctx.fillText(label, labelX, yPx);
      }
    }
    ctx.restore();
  }

  // Renko rotation LEGS: the zig-zag connecting consecutive pivots (start pivot -> end pivot),
  // coloured by direction (green up / red red) and weighted by reach tier (extended = bold,
  // short = thin dashed), with a pivot dot at each leg end. Mirrors the renko chart's legs on
  // the time axis. x = the CENTER of the candle containing each pivot's time.
  _drawRotationLegs(ctx, scope) {
    const rot = this._data.rotations;
    if (!rot) return;
    const legs = rot.legs || [];
    if (!legs.length) return;
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const O = this._options;
    const baseW = 1.5;
    const reachStyle = (reach) => {
      if (reach === 'extended') return { width: baseW * 2, dash: null };
      if (reach === 'short') return { width: 1, dash: [2, 3] };
      return { width: baseW, dash: null };   // 'typical' / unknown
    };
    const centerX = (ts) => {
      const e = this._candleEdgesForTime(ts, hpr);   // already bitmap-space (×hpr)
      return e ? (e.left + e.right) / 2 : null;
    };

    for (const l of legs) {
      // Skip legs before the loaded candle range (else _findCandleForTimestamp clamps them to
      // the first candle and draws a stray segment pinned at the left edge).
      if (this._candles && this._candles.length && l.start_ts < this._candles[0].time) continue;
      const x0 = centerX(l.start_ts);
      const x1 = centerX(l.end_ts);
      if (x0 === null || x1 === null) continue;       // a pivot scrolled out of range
      const y0c = this._series.priceToCoordinate(l.start_price);
      const y1c = this._series.priceToCoordinate(l.end_price);
      if (y0c == null || y1c == null) continue;
      const y0 = Math.round(y0c * vpr);
      const y1 = Math.round(y1c * vpr);
      const color = l.direction === 'up' ? O.legUpColor : O.legDownColor;
      const st = reachStyle(l.reach);
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1, Math.round(st.width * vpr));
      ctx.setLineDash(st.dash ? st.dash.map((d) => Math.round(d * hpr)) : []);
      ctx.beginPath();
      ctx.moveTo(Math.round(x0), y0);
      ctx.lineTo(Math.round(x1), y1);
      ctx.stroke();
      ctx.setLineDash([]);
      // pivot dot at the leg end
      const r = Math.max(1, Math.round(3 * hpr));
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(Math.round(x1), y1, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // Renko rotation reach levels: from each pivot, the p50 (teal) / p70 (slate) / p90 (amber)
  // leg-size percentiles projected in the leg's direction, spanning that leg's time. The
  // developing rotation (last pivot) also projects BOTH directions forward to the right edge,
  // labelled. Mirrors the renko chart (RenkoRotationsPage) on the time axis. endTime=null means
  // a forward (live) line that runs to the latest candle's right edge.
  _drawReachLevels(ctx, scope) {
    const rot = this._data.rotations;
    if (!rot || !rot.thresholds) return;
    const T = rot.thresholds;
    const legs = rot.legs || [];
    const timeScale = this._chart.timeScale();
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return;
    const O = this._options;
    const colorFor = (lvl) => (lvl === 90 ? O.reach90Color : (lvl === 50 ? O.reach50Color : O.reach70Color));
    const dash = [Math.round(4 * hpr), Math.round(4 * hpr)];
    const fmt = (v) => (v == null ? '' : (Math.round(v * 10) / 10).toFixed(1));

    const drawSeg = (price, startTime, endTime, lvl, label) => {
      if (price == null || !isFinite(price) || startTime == null) return;
      // Clip to the visible time range (endTime=null = forward to "now").
      if (endTime != null && (endTime < visibleRange.from || startTime > visibleRange.to + 600)) return;
      const y = this._series.priceToCoordinate(price);
      if (y === null || y === undefined) return;
      const se = this._candleEdgesForTime(startTime, hpr);
      let xs = se ? se.left : (startTime <= visibleRange.from ? 0 : null);
      if (xs === null) return;
      let xe;
      if (endTime == null) {
        xe = this._rightEdgePx(hpr, scope);
      } else {
        const ee = this._candleEdgesForTime(endTime, hpr);
        xe = ee ? ee.right : (endTime >= visibleRange.to ? scope.bitmapSize.width : 0);
      }
      if (xe < xs) xe = xs;
      const yPx = Math.round(y * vpr);
      const color = colorFor(lvl);
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1, Math.round(1 * vpr));
      ctx.setLineDash(dash);
      ctx.beginPath();
      ctx.moveTo(Math.round(xs), yPx);
      ctx.lineTo(Math.round(xe), yPx);
      ctx.stroke();
      if (label) {
        ctx.setLineDash([]);
        ctx.fillStyle = color;
        ctx.font = `${O.fontSize || 10}px sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.fillText(label, Math.round(xs) + Math.round(6 * hpr), yPx - Math.round(7 * hpr));
      }
      ctx.restore();
    };

    // Historic: each leg's p50/p70/p90 projected from its start pivot, spanning the leg.
    for (const l of legs) {
      if (this._candles && this._candles.length && l.start_ts < this._candles[0].time) continue;
      const t = T[l.direction];
      if (!t) continue;
      const sgn = l.direction === 'up' ? 1 : -1;
      if (t.p50 != null) drawSeg(l.start_price + sgn * t.p50, l.start_ts, l.end_ts, 50, null);
      if (t.p70 != null) drawSeg(l.start_price + sgn * t.p70, l.start_ts, l.end_ts, 70, null);
      if (t.p90 != null) drawSeg(l.start_price + sgn * t.p90, l.start_ts, l.end_ts, 90, null);
    }

    // Forward (developing): from the last pivot, project both directions to the right edge.
    const last = legs.length ? legs[legs.length - 1] : null;
    if (last) {
      const aPrice = last.end_price;
      const aTime = last.end_ts;
      if (T.up) {
        if (T.up.p50 != null) drawSeg(aPrice + T.up.p50, aTime, null, 50, `+50% (${fmt(T.up.p50)})`);
        if (T.up.p70 != null) drawSeg(aPrice + T.up.p70, aTime, null, 70, `+70% (${fmt(T.up.p70)})`);
        if (T.up.p90 != null) drawSeg(aPrice + T.up.p90, aTime, null, 90, `+90% (${fmt(T.up.p90)})`);
      }
      if (T.down) {
        if (T.down.p50 != null) drawSeg(aPrice - T.down.p50, aTime, null, 50, `-50% (${fmt(T.down.p50)})`);
        if (T.down.p70 != null) drawSeg(aPrice - T.down.p70, aTime, null, 70, `-70% (${fmt(T.down.p70)})`);
        if (T.down.p90 != null) drawSeg(aPrice - T.down.p90, aTime, null, 90, `-90% (${fmt(T.down.p90)})`);
      }
    }
  }

  // Draw secure/unsecure swing highs/lows as horizontal level lines (context/liquidity map).
  // UNSECURE = untapped/weak liquidity: bright (red high / blue low), extends to the right edge.
  // swept (grabbed, not BOS-confirmed) = orange. SECURE = confirmed/taken: faded grey, terminates
  // at the moment it became secure. Never a confluence weight - purely display context.
  // Market structure, rendered per the LuxAlgo PPAS scheme: ONE mark per swing at its
  // HIGHEST tier (PPAS re-texts the same label on promotion), marks colored by SIDE
  // (highs bull-green, lows bear-red), tier-3/2 = HH/HL/LH/LL text (normal/small), tier-1 =
  // small dot shown only via structShowDetail. BOS/CHoCH break lines: dashed t2 / solid t3
  // always (1px, tag text t3 only); dotted t1 lines follow structShowDetail with the dots.
  // Plus per-tier bias chips. Descriptive only: every element marks a REALIZED event (pivots
  // exist in the payload only once confirmed; break lines only once a 5m body closed through).
  _drawMarketStructure(ctx, scope) {
    const events = this._data.marketStructure;
    if (!events || events.length === 0) return;

    const timeScale = this._chart.timeScale();
    const hpr = scope.horizontalPixelRatio;
    const vpr = scope.verticalPixelRatio;
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return;
    const o = this._options;
    const detail = !!o.structShowDetail;
    const firstCandleTime = (this._candles && this._candles.length) ? this._candles[0].time : null;
    const centerX = (ts) => {
      const e = this._candleEdgesForTime(ts, hpr);   // already bitmap-space (x hpr)
      return e ? (e.left + e.right) / 2 : null;
    };

    // 1) Break lines (drawn first so pivot marks sit on top). Minor (tier-1) lines follow
    // the detail toggle like the minor swing dots (user decision 2026-07-13: a visible line
    // must have a visible origin mark); intermediate/major lines always draw.
    for (const e of events) {
      if (e.kind !== 'break') continue;
      if (e.tier === 1 && !detail) continue;
      // Skip only when the WHOLE line precedes the loaded candle range. A carry-in break
      // (prior-day pivot, in-range break) clamps its start to the canvas left edge EXPLICITLY:
      // _findCandleForTimestamp clamps early timestamps to the FIRST candle (non-null), so
      // falling through to _candleEdgesForTime would pin the line to the first candle's edge
      // instead of the pane edge.
      const pivotBeforeRange = firstCandleTime !== null && e.pivot_apex_ts_sec < firstCandleTime;
      if (pivotBeforeRange && e.break_ts_sec < firstCandleTime) continue;
      if (e.break_ts_sec < visibleRange.from || e.pivot_apex_ts_sec > visibleRange.to + 600) continue;

      const y = this._series.priceToCoordinate(e.level);
      if (y === null || y === undefined) continue;
      let xs;
      if (pivotBeforeRange) {
        xs = 0;
      } else {
        const se = this._candleEdgesForTime(e.pivot_apex_ts_sec, hpr);
        xs = se ? se.left : (e.pivot_apex_ts_sec <= visibleRange.from ? 0 : null);
      }
      if (xs === null) continue;
      const ee = this._candleEdgesForTime(e.break_ts_sec, hpr);
      let xe = ee ? ee.right : (e.break_ts_sec >= visibleRange.to ? scope.bitmapSize.width : 0);
      if (xe < xs) xe = xs;

      const yPx = Math.round(y * vpr);
      const color = e.dir === 1 ? o.structBullColor : o.structBearColor;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1, Math.round(vpr));   // PPAS width 1 for all tiers
      ctx.setLineDash(e.tier >= 3 ? []
        : e.tier >= 2 ? [Math.round(4 * hpr), Math.round(4 * hpr)]
          : [Math.round(2 * hpr), Math.round(3 * hpr)]);
      ctx.beginPath();
      ctx.moveTo(Math.round(xs), yPx);
      ctx.lineTo(Math.round(xe), yPx);
      ctx.stroke();
      ctx.setLineDash([]);
      // BOS/CHoCH tag text only on the LT tier: tier-1/2 breaks run ~40/13 per session at
      // the granular bottom tier and their tags would shingle into noise.
      if (e.tier >= 3) {
        ctx.fillStyle = color;
        ctx.font = `${o.fontSize || 10}px sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(e.tag, Math.round(xe) + Math.round(4 * hpr), yPx);
      }
      ctx.restore();
    }

    // 2) Pivot marks: ONE mark per swing at its HIGHEST tier (tier lists nest: a promoted
    // pivot appears in every lower tier at the same apex; PPAS re-texts the same label on
    // promotion). Tier-3/2 = HH/HL/LH/LL text, tier-1 = dot behind the detail toggle. Marks
    // are colored by SIDE (highs bull, lows bear), per PPAS.
    const topTier = new Map();
    for (const e of events) {
      if (e.kind !== 'pivot') continue;
      const k = e.side + ':' + e.apex_ts_sec;
      if ((topTier.get(k) || 0) < e.tier) topTier.set(k, e.tier);
    }
    for (const e of events) {
      if (e.kind !== 'pivot') continue;
      if (e.tier < (topTier.get(e.side + ':' + e.apex_ts_sec) || 0)) continue;
      if (e.tier === 1 && !detail) continue;
      if (firstCandleTime !== null && e.apex_ts_sec < firstCandleTime) continue;
      if (e.apex_ts_sec < visibleRange.from || e.apex_ts_sec > visibleRange.to + 600) continue;

      const x = centerX(e.apex_ts_sec);
      if (x === null) continue;
      const y = this._series.priceToCoordinate(e.price);
      if (y === null || y === undefined) continue;
      const yPx = Math.round(y * vpr);
      const above = e.side === 'high';

      ctx.save();
      ctx.fillStyle = above ? o.structBullColor : o.structBearColor;
      if (e.tier >= 2) {
        const major = e.tier >= 3;
        ctx.font = major
          ? `900 ${(o.fontSize || 10) + 4}px sans-serif`    // major: heaviest weight + larger
          : `${o.fontSize || 10}px sans-serif`;             // intermediate: regular
        ctx.textAlign = 'center';
        ctx.textBaseline = above ? 'bottom' : 'top';
        const ty = yPx + (above ? -1 : 1) * Math.round(6 * vpr);
        if (major) {
          // Dark halo so major labels stay legible over candles.
          ctx.strokeStyle = 'rgba(10, 12, 16, 0.85)';
          ctx.lineWidth = Math.max(2, Math.round(3 * hpr));
          ctx.lineJoin = 'round';
          ctx.strokeText(e.label, Math.round(x), ty);
        }
        ctx.fillText(e.label, Math.round(x), ty);
      } else {
        const r = Math.max(1, Math.round(1.5 * hpr));
        ctx.beginPath();
        ctx.arc(Math.round(x), yPx + (above ? -1 : 1) * Math.round(5 * vpr), r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // 3) Bias chips: the current per-tier latch state, pinned top-right of the pane.
    // User-facing names for the payload's tier field: 1 = Minor, 2 = Intermediate (Int),
    // 3 = Major.
    const TIER_NAME = { 1: 'Minor', 2: 'Int', 3: 'Major' };
    const biases = events.filter((e) => e.kind === 'bias').sort((a, b) => a.tier - b.tier);
    if (biases.length) {
      ctx.save();
      ctx.font = `bold ${(o.fontSize || 10) + 1}px sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      let yOff = Math.round(8 * vpr);
      for (const b of biases) {
        ctx.fillStyle = b.dir === 1 ? o.structBullColor : o.structBearColor;
        ctx.fillText(`${TIER_NAME[b.tier] || `T${b.tier}`} ${b.dir === 1 ? 'up' : 'down'}`,
          scope.bitmapSize.width - Math.round(8 * hpr), yOff);
        yOff += Math.round((o.fontSize || 10) * 1.6 * vpr);
      }
      ctx.restore();
    }
  }

}

export { OrderFlowPlugin };
